import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import TopBar from '../layout/TopBar';
import StatusBadge from '../components/StatusBadge';
import AdminHomeTab from '../components/admin/AdminHomeTab';
import GovernanceCycleTab from '../components/admin/GovernanceCycleTab';
import {
  ADMIN_USERS, ADMIN_MODULES, ADMIN_CHECKLIST_MASTER, RISK_CATEGORIES,
} from '../data/mockData';
import {
  Users, Layers, ListChecks, Tag, Building2, Plus, MoreHorizontal, Database,
  ArrowRight, Home, CalendarClock, Workflow, Network,
} from 'lucide-react';
import { cn } from '@/lib/utils';

const TABS = [
  { id: 'home', label: 'Admin Home', icon: Home },
  { id: 'cycle', label: 'Governance Cycle', icon: CalendarClock },
  { id: 'organogram', label: 'Organogram', icon: Building2 },
  { id: 'users', label: 'Users', icon: Users },
  { id: 'modules', label: 'Modules', icon: Layers },
  { id: 'checklist', label: 'Checklist Master', icon: ListChecks },
  { id: 'categories', label: 'Risk Categories', icon: Tag },
];

export default function AdminPage() {
  const [tab, setTab] = useState('home');
  const [modules, setModules] = useState(ADMIN_MODULES);
  const navigate = useNavigate();
  const toggleMod = (id) => setModules((m) => m.map((x) => (x.id === id ? { ...x, enabled: !x.enabled } : x)));

  return (
    <div>
      <TopBar
        title="Admin Configuration"
        subtitle="Manage organization, users, modules and master data"
        breadcrumbs={['HGS', 'Admin']}
        actions={
          <button
            onClick={() => navigate('/admin/masters')}
            className="inline-flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-[#CC0000] to-[#990000] hover:from-[#B00000] text-white text-sm font-semibold rounded-lg shadow-sm"
          >
            <Database className="w-4 h-4" />
            Master Configuration
            <ArrowRight className="w-4 h-4" />
          </button>
        }
      />
      <div className="p-8">
        <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
          {/* Tab nav */}
          <div className="px-2 pt-2 border-b border-slate-100 flex gap-1 overflow-x-auto">
            {TABS.map((t) => (
              <button
                key={t.id}
                onClick={() => setTab(t.id)}
                className={cn(
                  'inline-flex items-center gap-2 px-4 py-3 text-xs font-semibold rounded-t-lg transition border-b-2 whitespace-nowrap',
                  tab === t.id ? 'border-[#CC0000] text-[#CC0000] bg-red-50/30' : 'border-transparent text-slate-600 hover:text-slate-900'
                )}
              >
                <t.icon className="w-4 h-4" />
                {t.label}
              </button>
            ))}
          </div>

          <div className="p-6">
            {tab === 'home' && <AdminHomeTab onSwitchTab={setTab} />}
            {tab === 'cycle' && <GovernanceCycleTab />}

            {tab === 'organogram' && (
              <div className="space-y-4">
                <div className="flex items-start justify-between">
                  <div>
                    <h3 className="text-base font-bold text-slate-900">Organogram</h3>
                    <p className="text-xs text-slate-500">Upload your organization structure for role-based assignment</p>
                  </div>
                </div>
                <div className="border-2 border-dashed border-slate-300 rounded-xl p-12 text-center hover:border-[#CC0000] hover:bg-red-50/20 transition cursor-pointer">
                  <FileImage className="w-10 h-10 mx-auto text-slate-300 mb-3" />
                  <div className="text-sm font-semibold text-slate-700 mb-1">Drop organogram file here</div>
                  <div className="text-xs text-slate-500 mb-4">PDF, PNG or JPG up to 10MB</div>
                  <button className="inline-flex items-center gap-2 px-4 py-2 bg-slate-900 hover:bg-slate-800 text-white text-xs font-semibold rounded-lg transition">
                    <Upload className="w-3.5 h-3.5" />
                    Browse files
                  </button>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {[
                    { name: 'HMSI_organogram_2025.pdf', date: '2025-04-15', size: '2.4 MB' },
                    { name: 'Manufacturing_division.png', date: '2025-06-02', size: '1.1 MB' },
                    { name: 'R&D_structure.pdf', date: '2025-07-10', size: '3.2 MB' },
                  ].map((f) => (
                    <div key={f.name} className="p-4 rounded-lg border border-slate-200 hover:border-slate-300 transition">
                      <FileImage className="w-5 h-5 text-[#CC0000] mb-2" />
                      <div className="text-xs font-bold text-slate-900 truncate">{f.name}</div>
                      <div className="text-[10px] text-slate-500 mt-0.5">Uploaded {f.date} · {f.size}</div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {tab === 'users' && (
              <div>
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <h3 className="text-base font-bold text-slate-900">Users</h3>
                    <p className="text-xs text-slate-500">Manage user roles and division mapping</p>
                  </div>
                  <button className="inline-flex items-center gap-2 px-4 py-2 bg-[#CC0000] hover:bg-[#B00000] text-white text-xs font-semibold rounded-lg shadow-sm">
                    <Plus className="w-3.5 h-3.5" /> Add User
                  </button>
                </div>
                <div className="overflow-x-auto rounded-lg border border-slate-200">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-left bg-slate-50 border-b border-slate-200">
                        <th className="px-4 py-2.5 text-[10px] font-semibold text-slate-500 uppercase tracking-wider">User</th>
                        <th className="px-4 py-2.5 text-[10px] font-semibold text-slate-500 uppercase tracking-wider">Email</th>
                        <th className="px-4 py-2.5 text-[10px] font-semibold text-slate-500 uppercase tracking-wider">Role</th>
                        <th className="px-4 py-2.5 text-[10px] font-semibold text-slate-500 uppercase tracking-wider">Division</th>
                        <th className="px-4 py-2.5 text-[10px] font-semibold text-slate-500 uppercase tracking-wider">Status</th>
                        <th className="px-4 py-2.5" />
                      </tr>
                    </thead>
                    <tbody>
                      {ADMIN_USERS.map((u) => (
                        <tr key={u.id} className="border-b border-slate-100 hover:bg-slate-50/50">
                          <td className="px-4 py-3">
                            <div className="flex items-center gap-2">
                              <div className="w-7 h-7 rounded-full bg-slate-900 text-white flex items-center justify-center text-[10px] font-bold">
                                {u.name.split(' ').map((n) => n[0]).join('')}
                              </div>
                              <span className="text-sm font-semibold text-slate-900">{u.name}</span>
                            </div>
                          </td>
                          <td className="px-4 py-3 text-xs text-slate-600">{u.email}</td>
                          <td className="px-4 py-3 text-xs font-medium text-slate-700">{u.role}</td>
                          <td className="px-4 py-3 text-xs text-slate-600">{u.division}</td>
                          <td className="px-4 py-3"><StatusBadge status={u.status} /></td>
                          <td className="px-4 py-3">
                            <button className="w-7 h-7 rounded-md hover:bg-slate-100 flex items-center justify-center text-slate-500">
                              <MoreHorizontal className="w-4 h-4" />
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {tab === 'modules' && (
              <div>
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <h3 className="text-base font-bold text-slate-900">Governance Modules</h3>
                    <p className="text-xs text-slate-500">Enable or disable modules for your organization</p>
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {modules.map((m) => (
                    <div key={m.id} className="p-4 rounded-xl border border-slate-200 hover:border-slate-300 transition flex items-center justify-between">
                      <div>
                        <div className="text-sm font-bold text-slate-900">{m.name}</div>
                        <div className="text-xs text-slate-500 mt-0.5">Owner: {m.owner} · {m.items} items</div>
                      </div>
                      <button
                        onClick={() => toggleMod(m.id)}
                        className={cn(
                          'relative w-11 h-6 rounded-full transition',
                          m.enabled ? 'bg-[#CC0000]' : 'bg-slate-300'
                        )}
                      >
                        <span className={cn('absolute top-0.5 w-5 h-5 rounded-full bg-white shadow transition', m.enabled ? 'left-5' : 'left-0.5')} />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {tab === 'checklist' && (
              <div>
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <h3 className="text-base font-bold text-slate-900">Checklist Master</h3>
                    <p className="text-xs text-slate-500">Manage section-wise checklists across modules</p>
                  </div>
                  <button className="inline-flex items-center gap-2 px-4 py-2 bg-[#CC0000] hover:bg-[#B00000] text-white text-xs font-semibold rounded-lg shadow-sm">
                    <Plus className="w-3.5 h-3.5" /> New Section
                  </button>
                </div>
                <div className="overflow-x-auto rounded-lg border border-slate-200">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-left bg-slate-50 border-b border-slate-200">
                        <th className="px-4 py-2.5 text-[10px] font-semibold text-slate-500 uppercase tracking-wider">Module</th>
                        <th className="px-4 py-2.5 text-[10px] font-semibold text-slate-500 uppercase tracking-wider">Section</th>
                        <th className="px-4 py-2.5 text-[10px] font-semibold text-slate-500 uppercase tracking-wider text-center">Question Count</th>
                        <th className="px-4 py-2.5 text-[10px] font-semibold text-slate-500 uppercase tracking-wider">Last Updated</th>
                        <th className="px-4 py-2.5" />
                      </tr>
                    </thead>
                    <tbody>
                      {ADMIN_CHECKLIST_MASTER.map((c) => (
                        <tr key={c.id} className="border-b border-slate-100 hover:bg-slate-50/50">
                          <td className="px-4 py-3 text-xs font-semibold text-slate-900">{c.module}</td>
                          <td className="px-4 py-3 text-xs text-slate-700">{c.section}</td>
                          <td className="px-4 py-3 text-center">
                            <span className="inline-flex items-center justify-center w-8 h-7 rounded-md bg-slate-100 text-slate-700 font-bold text-xs">{c.count}</span>
                          </td>
                          <td className="px-4 py-3 text-xs text-slate-500">{c.lastUpdated}</td>
                          <td className="px-4 py-3">
                            <button className="text-xs font-semibold text-[#CC0000] hover:underline">Edit</button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {tab === 'categories' && (
              <div>
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <h3 className="text-base font-bold text-slate-900">Risk Categories</h3>
                    <p className="text-xs text-slate-500">Standard taxonomy used across all risk records</p>
                  </div>
                  <button className="inline-flex items-center gap-2 px-4 py-2 bg-[#CC0000] hover:bg-[#B00000] text-white text-xs font-semibold rounded-lg shadow-sm">
                    <Plus className="w-3.5 h-3.5" /> Add Category
                  </button>
                </div>
                <div className="flex flex-wrap gap-2">
                  {RISK_CATEGORIES.map((c, i) => (
                    <div key={c} className="px-3 py-2 rounded-lg border border-slate-200 bg-white hover:border-[#CC0000] transition flex items-center gap-2">
                      <span className="w-1.5 h-1.5 rounded-full bg-[#CC0000]" />
                      <span className="text-xs font-semibold text-slate-700">{c}</span>
                      <span className="text-[10px] text-slate-400">({Math.max(2, (i * 7) % 23)} risks)</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
